import React, { useState, useMemo } from 'react';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import ListGroup from 'react-bootstrap/ListGroup';
import { useFetch } from '../hooks/useApiCall';
import { getCategories } from '../services/serviceService';
import { mapCategoriesToPriceData } from '../utils/dataMappers';
import LoadingSpinner from './LoadingSpinner';
import ErrorAlert from './ErrorAlert';
import data from '../data/paomakeup';

const ServiceSelector = ({
  onServiceSelect,
  onServicesChange,
  multiple = false,
  showQuantity = false,
}) => {
  const { data: apiCategories, loading, error, refetch } = useFetch(getCategories);

  const priceData = useMemo(
    () => apiCategories ? mapCategoriesToPriceData(apiCategories) : data.our_services_price,
    [apiCategories]
  );

  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSubCategory, setSelectedSubCategory] = useState(null);
  const [addedServices, setAddedServices] = useState([]);
  const [quantity, setQuantity] = useState(1);

  // Pick a service item directly
  const pickService = (serviceName, serviceId) => {
    const catName = selectedCategory?.service_name || '';
    const subName = selectedSubCategory?.sub_service_name || '';
    let label = catName;
    if (subName) label += ' / ' + subName;
    label += ' / ' + serviceName;

    if (!multiple) {
      // Single mode: notify parent immediately
      if (onServiceSelect) onServiceSelect(label, serviceId);
    }

    return { label, serviceId };
  };

  const handleAddService = (serviceName, serviceId) => {
    const { label } = pickService(serviceName, serviceId);
    let displayLabel = label;
    if (showQuantity && quantity > 1) {
      displayLabel += ` - ${quantity} persona${quantity > 1 ? 's' : ''}`;
    }

    const newEntry = { label: displayLabel, serviceId, quantity: showQuantity ? quantity : 1 };
    const updated = [...addedServices, newEntry];
    setAddedServices(updated);
    if (onServicesChange) onServicesChange(updated);

    // Reset selection
    setSelectedCategory(null);
    setSelectedSubCategory(null);
    setQuantity(1);
  };

  const handleRemoveService = (idx) => {
    const updated = addedServices.filter((_, i) => i !== idx);
    setAddedServices(updated);
    if (onServicesChange) onServicesChange(updated);
  };

  // Get the list of services to show based on current selection
  const getVisibleServices = () => {
    if (!selectedCategory) return [];
    if (selectedCategory.sub_services?.length > 0 && selectedSubCategory) {
      return selectedSubCategory.services || [];
    }
    if (!selectedCategory.sub_services?.length && selectedCategory.services?.length > 0) {
      return selectedCategory.services;
    }
    return [];
  };

  const visibleServices = getVisibleServices();

  if (loading) return <LoadingSpinner />;
  if (error && !apiCategories) return <ErrorAlert message={error} onRetry={refetch} />;

  return (
    <div>
      {/* Step A: Category buttons */}
      <Row className="g-2 mb-3">
        {priceData.map((cat, idx) => (
          <Col xs={6} md={4} key={cat.service_slug || idx}>
            <Button
              variant={selectedCategory?.service_slug === cat.service_slug ? 'primary' : 'outline-primary'}
              className="w-100 py-2"
              onClick={() => {
                setSelectedCategory(cat);
                setSelectedSubCategory(null);
              }}
            >
              {cat.service_name}
            </Button>
          </Col>
        ))}
      </Row>

      {/* Step B: Sub-category list (if category has sub_services) */}
      {selectedCategory?.sub_services?.length > 0 && (
        <div className="mb-3">
          <small className="text-muted d-block mb-2">Sub-categorias de {selectedCategory.service_name}:</small>
          <ListGroup>
            {selectedCategory.sub_services.map((sub, idx) => (
              <ListGroup.Item
                key={sub.sub_service_name || idx}
                action
                active={selectedSubCategory?.sub_service_name === sub.sub_service_name}
                onClick={() => setSelectedSubCategory(sub)}
              >
                {sub.sub_service_name}
                {sub.description && <small className="d-block text-muted">{sub.description}</small>}
              </ListGroup.Item>
            ))}
          </ListGroup>
        </div>
      )}

      {/* Step C: Service items */}
      {visibleServices.length > 0 && (
        <div className="mb-3">
          <small className="text-muted d-block mb-2">Seleccione un servicio:</small>
          <ListGroup>
            {visibleServices.map((svc, idx) => (
              <ListGroup.Item
                key={svc.name || idx}
                action
                className="d-flex justify-content-between align-items-center"
                onClick={() => {
                  if (multiple) {
                    handleAddService(svc.name, svc.id || null);
                  } else {
                    pickService(svc.name, svc.id || null);
                  }
                }}
              >
                <span>{svc.name}</span>
                {svc.price && <span className="text-muted">{svc.price}</span>}
              </ListGroup.Item>
            ))}
          </ListGroup>
        </div>
      )}

      {/* Quantity input for multiple mode */}
      {multiple && showQuantity && selectedCategory && visibleServices.length > 0 && (
        <Form.Group className="mb-3" controlId="servicePeopleCount">
          <Form.Label>Cantidad de personas para este servicio</Form.Label>
          <Form.Control
            type="number"
            min={1}
            value={quantity}
            onChange={e => setQuantity(e.target.value ? Number(e.target.value) : '')}
          />
        </Form.Group>
      )}

      {/* Added services list (multiple mode) */}
      {multiple && addedServices.length > 0 && (
        <div className="mb-3">
          <strong>Servicios agregados:</strong>
          <ul className="ps-0 list-unstyled">
            {addedServices.map((srv, idx) => (
              <li key={idx} className="d-flex align-items-center py-1">
                <span style={{ flex: 1 }}>{srv.label}</span>
                <Button
                  variant="danger"
                  size="sm"
                  className="ms-2"
                  onClick={() => handleRemoveService(idx)}
                >
                  Quitar
                </Button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default ServiceSelector;
